import type {
  CreateSavedViewDto,
  SavedView,
} from '../types';
import type {
  ColumnConfiguration,
  CustomView,
  FilterConfiguration,
  UserTableColumnPreference,
  UserTablePreference,
} from '../types/filters';

const MIGRATED_FLAG_PREFIX = 'cloudincidents_prefs_migrated_';
const LEGACY_VIEWS_PREFIX = 'cloudincidents_views_';
const LEGACY_COLUMNS_PREFIX = 'cloudincidents_columns_';
const EMPTY_FILTER_CONFIGURATION: FilterConfiguration = {
  rootGroup: { id: 'root', logicalOperator: 'AND', conditions: [], groups: [] },
};

type TablePreferencePayload = Omit<UserTablePreference, 'tableId' | 'updatedAt'>;

export interface PreferencesMigrationApiClient {
  getSavedViews(tableType: string): Promise<SavedView[]>;
  createSavedView(data: CreateSavedViewDto): Promise<SavedView>;
  upsertUserTablePreference(tableId: string, prefs: TablePreferencePayload): Promise<UserTablePreference>;
}

export interface PreferencesMigrationResult {
  views: number;
  columns: number;
  errors: number;
}

interface ErrorWithResponseStatus {
  response?: {
    status?: number;
  };
}

function getMigratedFlagKey(tenantId: string, userId: string): string {
  return `${MIGRATED_FLAG_PREFIX}${tenantId}_${userId}`;
}

function getErrorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined;
  }

  return (error as ErrorWithResponseStatus).response?.status;
}

function parseJson<T>(key: string, value: string | null): T {
  if (!value) {
    throw new Error(`Legacy preference key ${key} is empty.`);
  }

  return JSON.parse(value) as T;
}

function normalizeName(name: string): string {
  return name.trim().toLocaleLowerCase();
}

function buildSavedViewPayload(tableType: string, view: CustomView): CreateSavedViewDto {
  return {
    tableType,
    name: view.name,
    description: view.description,
    isDefault: view.isDefault ?? false,
    isPublic: view.isShared ?? view.sharing === 'public',
    filterConfigJson: JSON.stringify(view.filters ?? EMPTY_FILTER_CONFIGURATION),
    columnConfigJson: JSON.stringify(view.columns ?? ({ columns: [] } satisfies ColumnConfiguration)),
    groupByConfigJson: view.groupByConfig ? JSON.stringify(view.groupByConfig) : undefined,
    isExpanded: view.isExpanded,
    sharedWithUserIds: view.sharedWithUserIds,
    sharedWithTeamIds: view.sharedWithTeamIds,
    sharedWithCustomerIds: view.sharedWithCustomerIds,
  };
}

function toColumnPreferences(value: unknown): UserTableColumnPreference[] {
  if (!Array.isArray(value)) {
    throw new Error('Legacy column preferences must be an array.');
  }

  return value.map((column) => {
    if (typeof column !== 'object' || column === null) {
      throw new Error('Legacy column preference entries must be objects.');
    }

    const item = column as Partial<UserTableColumnPreference> & { field?: string };
    const id = typeof item.id === 'string' && item.id.trim() ? item.id : item.field;
    if (!id) {
      throw new Error('Legacy column preference entry is missing an id.');
    }

    return {
      id,
      visible: item.visible !== false,
      order: typeof item.order === 'number' ? item.order : 0,
      width: typeof item.width === 'number' ? item.width : undefined,
      pinned: item.pinned === true,
    };
  });
}

async function migrateViewsKey(
  key: string,
  tableType: string,
  apiClient: PreferencesMigrationApiClient,
): Promise<{ migrated: number; success: boolean }> {
  const views = parseJson<CustomView[]>(key, localStorage.getItem(key));
  if (!Array.isArray(views)) {
    throw new Error(`Legacy saved views key ${key} did not contain an array.`);
  }

  const existingViews = await apiClient.getSavedViews(tableType);
  const existingNames = new Set(existingViews.map((view) => normalizeName(view.name)));
  let migrated = 0;

  for (const view of views) {
    if (!view?.name?.trim()) {
      continue;
    }

    if (existingNames.has(normalizeName(view.name))) {
      migrated += 1;
      continue;
    }

    try {
      await apiClient.createSavedView(buildSavedViewPayload(tableType, view));
      existingNames.add(normalizeName(view.name));
      migrated += 1;
    } catch (error) {
      if (getErrorStatus(error) === 409) {
        existingNames.add(normalizeName(view.name));
        migrated += 1;
        continue;
      }

      throw error;
    }
  }

  return { migrated, success: true };
}

async function migrateColumnsKey(
  key: string,
  tableId: string,
  apiClient: PreferencesMigrationApiClient,
): Promise<void> {
  const parsed = parseJson<unknown>(key, localStorage.getItem(key));
  const columns = toColumnPreferences(parsed);
  await apiClient.upsertUserTablePreference(tableId, { columns, density: 'comfortable' });
}

export async function migrateLegacyPreferences(
  tenantId: string,
  userId: string,
  apiClient: PreferencesMigrationApiClient,
): Promise<PreferencesMigrationResult> {
  const result: PreferencesMigrationResult = { views: 0, columns: 0, errors: 0 };
  const migratedFlagKey = getMigratedFlagKey(tenantId, userId);

  if (typeof localStorage === 'undefined' || localStorage.getItem(migratedFlagKey) === '1') {
    return result;
  }

  const viewsPrefix = `${LEGACY_VIEWS_PREFIX}${tenantId}_${userId}_`;
  const columnsPrefix = `${LEGACY_COLUMNS_PREFIX}${tenantId}_${userId}_`;
  const keys = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))
    .filter((key): key is string => Boolean(key));

  for (const key of keys.filter((item) => item.startsWith(viewsPrefix))) {
    const tableType = key.slice(viewsPrefix.length);
    try {
      const migratedViews = await migrateViewsKey(key, tableType, apiClient);
      result.views += migratedViews.migrated;
      localStorage.removeItem(key);
    } catch (error) {
      result.errors += 1;
      console.warn('Failed to migrate legacy saved views', { tenantId, userId, key, error });
    }
  }

  for (const key of keys.filter((item) => item.startsWith(columnsPrefix))) {
    const tableId = key.slice(columnsPrefix.length);
    try {
      await migrateColumnsKey(key, tableId, apiClient);
      result.columns += 1;
      localStorage.removeItem(key);
    } catch (error) {
      result.errors += 1;
      console.warn('Failed to migrate legacy column preferences', { tenantId, userId, key, error });
    }
  }

  if (result.errors === 0) {
    localStorage.setItem(migratedFlagKey, '1');
  }

  return result;
}
